import { useRef } from 'react';

import { getSessionIDAsync } from 'redline-client-sdk';
import { useRedline } from '@libs/redline';
import { getCookie, addCookie } from '@libs/utils/helpers/getSafeCookies';

import { useServerEventsContext } from './useServerEventsContext';
import { BridgeType } from '../context';

export type AppSessionTrackingCallbacks = {
  onSessionStarted?: () => void;
  onInfoTracked?: (payload: Record<string, unknown>) => void;
};

const waitForRedlineReady = (timeout = 7000) =>
  new Promise<void>((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any)?.cpRedline?.ready) {
      resolve();
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any)?.cpRedline?.ready || Date.now() - start >= timeout) {
        clearInterval(interval);
        resolve();
      }
    }, 100);
  });

export const useAppSessionTracking = (bridge: BridgeType) => {
  const { track } = useRedline();
  const { getServerContext } = useServerEventsContext(bridge);
  const hasRun = useRef(false);

  const fireTracking = async (callbacks?: AppSessionTrackingCallbacks) => {
    if (!bridge.isWebViewBridgeAvailable) return;
    if (hasRun.current) return;
    hasRun.current = true;

    try {
      await waitForRedlineReady();
      const searchParams = new URLSearchParams(window.location.search);
      const sessionId = searchParams.get('session_id') || '';

      let storeAppVersion = 'unknown';
      try {
        const updateInfo = await bridge.inAppUpdatesCheckForUpdate();
        storeAppVersion = updateInfo.storeVersion;
      } catch (updateError) {
        const msg = updateError instanceof Error ? updateError.message : String(updateError);
        console.error('[AppTracking] Error fetching storeAppVersion:', msg);
      }

      const nativeContext = await getServerContext();

      const device = bridge.store.getState().device;

      const deviceInfo = {
        type: device?.type,
        brand: nativeContext?.deviceBrand ?? device?.brand,
        model: nativeContext?.deviceModel ?? device?.model,
        os: nativeContext?.platform ?? device?.os,
        version: nativeContext?.osVersion ?? device?.osVersion,
      };

      if (!getCookie('rl_ass', true)) {
        try {
          await track.mobile.appSessionStarted({ mobileSessionId: sessionId });
        } catch (e) {
          const msg = e instanceof Error ? e.message : JSON.stringify(e);
          console.error('[AppTracking] appSessionStarted failed:', msg);
        }
        callbacks?.onSessionStarted?.();

        try {
          const webSessionId = await getSessionIDAsync();
          const expires = new Date();
          expires.setMinutes(expires.getMinutes() + 30);
          addCookie({ key: 'rl_ass', value: { mobileSessionId: sessionId, webSessionId }, encrypt: true, expires });
        } catch (e) {
          const msg = e instanceof Error ? e.message : JSON.stringify(e);
          console.error('[AppTracking] getSessionIDAsync/addCookie failed:', msg);
        }
      }

      const infoPayload: Record<string, unknown> = {
        deviceInfo,
        storeAppVersion,
        appVersion: nativeContext?.appVersion,
        appBuild: nativeContext?.appBuild,
        appInstanceId: nativeContext?.appInstanceId,
        appsFlyerId: nativeContext?.appsFlyerId,
        advertisingId: nativeContext?.advertisingId,
        attStatus: nativeContext?.attStatus,
        userAgent: nativeContext?.userAgent,
        screenWidth: nativeContext?.screenWidth,
        screenHeight: nativeContext?.screenHeight,
      };

      try {
        track.mobile.appInfoTracked(infoPayload);
      } catch (e) {
        const msg = e instanceof Error ? e.message : JSON.stringify(e);
        console.error('[AppTracking] appInfoTracked failed:', msg);
      }
      callbacks?.onInfoTracked?.(infoPayload);
    } catch (e) {
      const msg = e instanceof Error ? e.message : JSON.stringify(e);
      console.error('[AppTracking] General error in tracking:', msg);
    }
  };

  return { fireTracking };
};
